import Debug from '../utils/Debug.js';

/**
 * AudioControlPanel - UI for controlling audio system settings
 *
 * Features:
 * - Master volume controls (music, SFX, UI)
 * - Per-category mix controls (footsteps, combat, voices, fire)
 * - Sound test buttons with file picker
 * - Music seed/tempo controls
 */
export default class AudioControlPanel {
    constructor(audioSystem, container) {
        Debug.log('AudioControlPanel', 'Initializing...', {
            hasAudioSystem: !!audioSystem,
            hasContainer: !!container,
            containerParent: container?.parentElement?.tagName
        });

        this.audioSystem = audioSystem;
        this.container = container;
        this.audio = audioSystem ? audioSystem.getAudioManager() : null;
        this.game = null; // Set later via setGame()

        this.createUI();
        this.updateValues();

        Debug.log('AudioControlPanel', 'Initialized. Controls created:',
            this.container?.querySelector('.audio-control-panel') ? 'YES' : 'NO');
    }

    setGame(game) {
        this.game = game;
    }

    createUI() {
        if (!this.container) {
            Debug.error('AudioControlPanel', 'Container is null!');
            return;
        }

        this.container.innerHTML = `
            <div class="audio-control-panel">
                <h3>Audio Controls</h3>

                <!-- Master Volume Controls -->
                <div class="audio-section">
                    <h4>Master Volume</h4>

                    <div class="slider-control">
                        <label>Music Volume</label>
                        <input type="range" id="music-volume" min="0" max="100" value="30" />
                        <span id="music-volume-value">30%</span>
                    </div>

                    <div class="slider-control">
                        <label>SFX Volume</label>
                        <input type="range" id="sfx-volume" min="0" max="100" value="50" />
                        <span id="sfx-volume-value">50%</span>
                    </div>

                    <div class="slider-control">
                        <label>UI Volume</label>
                        <input type="range" id="ui-volume" min="0" max="100" value="100" />
                        <span id="ui-volume-value">100%</span>
                    </div>

                    <button id="mute-sfx">Mute SFX</button>
                </div>

                <!-- World SFX Mix Controls -->
                <div class="audio-section">
                    <h4>World SFX Mix</h4>

                    <div class="slider-control">
                        <label>Player Footsteps</label>
                        <input type="range" id="player-footstep-gain" min="0" max="200" value="100" />
                        <span id="player-footstep-gain-value">100%</span>
                    </div>

                    <div class="slider-control">
                        <label>NPC Footsteps</label>
                        <input type="range" id="npc-footstep-gain" min="0" max="200" value="100" />
                        <span id="npc-footstep-gain-value">100%</span>
                    </div>

                    <div class="slider-control">
                        <label>Combat Sounds</label>
                        <input type="range" id="combat-gain" min="0" max="200" value="100" />
                        <span id="combat-gain-value">100%</span>
                    </div>

                    <div class="slider-control">
                        <label>NPC Voices</label>
                        <input type="range" id="npc-voice-gain" min="0" max="200" value="100" />
                        <span id="npc-voice-gain-value">100%</span>
                    </div>

                    <div class="slider-control">
                        <label>Fire Sounds</label>
                        <input type="range" id="fire-gain" min="0" max="200" value="100" />
                        <span id="fire-gain-value">100%</span>
                    </div>
                </div>

                <!-- Distance Range Controls -->
                <div class="audio-section">
                    <h4>Sound Distance Range</h4>

                    <div class="slider-control">
                        <label>Footstep Range</label>
                        <input type="range" id="footstep-distance" min="10" max="400" value="100" />
                        <span id="footstep-distance-value">100%</span>
                    </div>

                    <div class="slider-control">
                        <label>Combat Range</label>
                        <input type="range" id="combat-distance" min="10" max="400" value="100" />
                        <span id="combat-distance-value">100%</span>
                    </div>

                    <div class="slider-control">
                        <label>Voice Range</label>
                        <input type="range" id="npc-voice-distance" min="10" max="400" value="100" />
                        <span id="npc-voice-distance-value">100%</span>
                    </div>

                    <div class="slider-control">
                        <label>Fire Range</label>
                        <input type="range" id="fire-distance" min="10" max="400" value="100" />
                        <span id="fire-distance-value">100%</span>
                    </div>
                </div>

                <!-- Music Controls -->
                <div class="audio-section">
                    <h4>Music Generator</h4>

                    <div class="slider-control">
                        <label>Tempo (BPM)</label>
                        <input type="range" id="music-tempo" min="40" max="160" value="80" />
                        <span id="music-tempo-value">80</span>
                    </div>

                    <div class="slider-control">
                        <label>Music Seed</label>
                        <input type="number" id="music-seed" min="0" max="65535" value="1234" />
                        <button id="random-seed">Random</button>
                    </div>

                    <div class="button-group">
                        <button id="start-music">Start Music</button>
                        <button id="stop-music">Stop Music</button>
                    </div>
                </div>

                <!-- Sound Test -->
                <div class="audio-section">
                    <h4>Sound Test</h4>

                    <div class="sound-test">
                        <label>Test Sound Category:</label>
                        <select id="test-category">
                            <option value="footstep">Footstep (Light)</option>
                            <option value="footstep-heavy">Footstep (Heavy)</option>
                            <option value="punch">Punch</option>
                            <option value="kick">Kick</option>
                            <option value="ui-click">UI Click</option>
                            <option value="ui-open">UI Open</option>
                            <option value="ui-confirm">UI Confirm</option>
                        </select>
                        <button id="play-test-sound">Play</button>
                    </div>

                    <div class="custom-sound-test">
                        <label>Custom Sound File:</label>
                        <input type="text" id="custom-sound-path" placeholder="audio/sfx/footsteps/footstep_dirt_light_01.wav" />
                        <button id="play-custom-sound">Play Custom</button>
                    </div>
                </div>

                <!-- Footstep Timing -->
                <div class="audio-section">
                    <h4>Footstep Timing</h4>

                    <div class="slider-control">
                        <label>Walk Step Interval</label>
                        <input type="range" id="walk-step-interval" min="50" max="300" value="167" />
                        <span id="walk-step-interval-value">1.67</span>
                    </div>

                    <div class="slider-control">
                        <label>Run Step Interval</label>
                        <input type="range" id="run-step-interval" min="50" max="300" value="250" />
                        <span id="run-step-interval-value">2.50</span>
                    </div>
                </div>
            </div>
        `;

        this.attachEventListeners();
    }

    attachEventListeners() {
        if (!this.audio) {
            Debug.warn('AudioControlPanel', 'Audio manager not available, event listeners not attached');
            return;
        }
        if (!this.container) {
            Debug.error('AudioControlPanel', 'Container is null in attachEventListeners!');
            return;
        }

        // Music Volume
        const musicVolume = this.container.querySelector('#music-volume');
        const musicVolumeValue = this.container.querySelector('#music-volume-value');
        musicVolume.addEventListener('input', (e) => {
            const val = e.target.value / 100;
            this.audio.setMusicVolume(val);
            musicVolumeValue.textContent = `${e.target.value}%`;
        });

        // SFX Volume
        const sfxVolume = this.container.querySelector('#sfx-volume');
        const sfxVolumeValue = this.container.querySelector('#sfx-volume-value');
        sfxVolume.addEventListener('input', (e) => {
            const val = e.target.value / 100;
            this.audio.setSfxVolume(val);
            sfxVolumeValue.textContent = `${e.target.value}%`;
        });

        // UI Volume
        const uiVolume = this.container.querySelector('#ui-volume');
        const uiVolumeValue = this.container.querySelector('#ui-volume-value');
        uiVolume.addEventListener('input', (e) => {
            const val = e.target.value / 100;
            this.audio.setUiSfxVolume(val);
            uiVolumeValue.textContent = `${e.target.value}%`;
        });

        // Mute SFX
        const muteSfx = this.container.querySelector('#mute-sfx');
        muteSfx.addEventListener('click', () => {
            const isMuted = this.audio.isSfxMuted();
            this.audio.setSfxMuted(!isMuted);
            muteSfx.textContent = isMuted ? 'Mute SFX' : 'Unmute SFX';
        });

        // World Mix Controls
        this.attachMixControl('player-footstep-gain', 'playerFootstepGain', 0.01);
        this.attachMixControl('npc-footstep-gain', 'npcFootstepGain', 0.01);
        this.attachMixControl('combat-gain', 'combatGain', 0.01);
        this.attachMixControl('npc-voice-gain', 'npcVoiceGain', 0.01);
        this.attachMixControl('fire-gain', 'fireGain', 0.01);

        // Distance Controls
        this.attachMixControl('footstep-distance', 'footstepDistanceMul', 0.01);
        this.attachMixControl('combat-distance', 'combatDistanceMul', 0.01);
        this.attachMixControl('npc-voice-distance', 'npcVoiceDistanceMul', 0.01);
        this.attachMixControl('fire-distance', 'fireDistanceMul', 0.01);

        // Music Controls
        const musicTempo = this.container.querySelector('#music-tempo');
        const musicTempoValue = this.container.querySelector('#music-tempo-value');
        musicTempo.addEventListener('input', (e) => {
            this.audio.setTempo(Number(e.target.value));
            musicTempoValue.textContent = e.target.value;
        });

        const musicSeed = this.container.querySelector('#music-seed');
        musicSeed.addEventListener('change', (e) => {
            this.audio.setSeed(Number(e.target.value));
        });

        this.container.querySelector('#random-seed').addEventListener('click', () => {
            const seed = Math.floor(Math.random() * 65536);
            musicSeed.value = seed;
            this.audio.setSeed(seed);
        });

        this.container.querySelector('#start-music').addEventListener('click', () => {
            this.audio.startMusic();
        });

        this.container.querySelector('#stop-music').addEventListener('click', () => {
            this.audio.stopMusic();
        });

        // Sound Test
        this.container.querySelector('#play-test-sound').addEventListener('click', () => {
            const category = this.container.querySelector('#test-category').value;
            this.playTestSound(category);
        });

        this.container.querySelector('#play-custom-sound').addEventListener('click', () => {
            const path = this.container.querySelector('#custom-sound-path').value;
            if (path) this.playCustomSound(path);
        });

        // Footstep Timing
        const walkInterval = this.container.querySelector('#walk-step-interval');
        const walkIntervalValue = this.container.querySelector('#walk-step-interval-value');
        walkInterval.addEventListener('input', (e) => {
            const val = e.target.value / 100;
            if (this.audioSystem) {
                this.audioSystem._walkStepInterval = val;
            }
            walkIntervalValue.textContent = val.toFixed(2);
        });

        const runInterval = this.container.querySelector('#run-step-interval');
        const runIntervalValue = this.container.querySelector('#run-step-interval-value');
        runInterval.addEventListener('input', (e) => {
            const val = e.target.value / 100;
            if (this.audioSystem) {
                this.audioSystem._runStepInterval = val;
            }
            runIntervalValue.textContent = val.toFixed(2);
        });
    }

    attachMixControl(sliderId, paramKey, multiplier) {
        const slider = this.container.querySelector(`#${sliderId}`);
        const valueSpan = this.container.querySelector(`#${sliderId}-value`);

        slider.addEventListener('input', (e) => {
            const val = e.target.value * multiplier;
            this.audio.setWorldMixParam(paramKey, val);
            valueSpan.textContent = `${e.target.value}%`;
        });
    }

    playTestSound(category) {
        if (!this.audio || !this.audioSystem) return;

        // Get player position (or default to origin)
        const player = this.game?.player;
        const pos = player ? { x: player.x, y: player.y + 1, z: player.z } : { x: 0, y: 1, z: 0 };

        switch (category) {
            case 'footstep':
                this.audio.playWorldFootstep({
                    worldPos: pos,
                    source: 'player',
                    isHeavy: false
                });
                break;
            case 'footstep-heavy':
                this.audio.playWorldFootstep({
                    worldPos: pos,
                    source: 'player',
                    isHeavy: true
                });
                break;
            case 'punch':
            case 'kick':
                this.audio.playWorldWeaponSwing({
                    worldPos: pos,
                    weaponType: 'blunt'
                });
                setTimeout(() => {
                    this.audio.playWorldMeleeImpact({
                        worldPos: pos,
                        targetType: 'flesh'
                    });
                }, 150);
                break;
            case 'ui-click':
                this.audio.playUiClick();
                break;
            case 'ui-open':
                this.audio.playUiOpen();
                break;
            case 'ui-confirm':
                this.audio.playUiConfirm();
                break;
        }
    }

    playCustomSound(path) {
        if (!this.audio || !this.audio.sfx) return;

        // Play the custom file through the UI bus
        this.audio.sfx.playOneShot({
            url: path,
            destination: this.audio.uiBusGain || this.audio.sfxMasterGain,
            gain: 1.0,
            playbackRate: 1.0,
            playbackRateJitter: 0.02,
            attack: 0.005,
            release: 0.08
        }).catch(err => {
            Debug.error('AudioControlPanel', 'Failed to play custom sound:', err);
            alert(`Failed to load sound: ${path}`);
        });
    }

    updateValues() {
        if (!this.audio) return;

        // Update displayed values from current audio state
        const musicVol = Math.round(this.audio.getMusicVolume() * 100);
        const sfxVol = Math.round(this.audio.getSfxVolume() * 100);
        const uiVol = Math.round(this.audio.getUiSfxVolume() * 100);

        this.container.querySelector('#music-volume').value = musicVol;
        this.container.querySelector('#music-volume-value').textContent = `${musicVol}%`;
        this.container.querySelector('#sfx-volume').value = sfxVol;
        this.container.querySelector('#sfx-volume-value').textContent = `${sfxVol}%`;
        this.container.querySelector('#ui-volume').value = uiVol;
        this.container.querySelector('#ui-volume-value').textContent = `${uiVol}%`;

        const muteBtn = this.container.querySelector('#mute-sfx');
        muteBtn.textContent = this.audio.isSfxMuted() ? 'Unmute SFX' : 'Mute SFX';
    }
}
